import { useState, type FormEvent } from "react";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dah83gboh653ivfvnnn0";

type Status = "idle" | "submitting" | "success" | "error";

interface ServerResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") {
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);

    const trap = String(formData.get("website_alt") || "").trim();
    if (trap) {
      setStatus("success");
      setFormError("");
      form.reset();
      setMessageLength(0);
      return;
    }
    formData.delete("website_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") {
        payload.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: ServerResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as ServerResponse;
      } catch {
        parsed = null;
      }

      const rawMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const messageText = typeof rawMessage === "string" ? rawMessage : "";
      const hasSpamFlag = messageText.toLowerCase().includes("spam");
      const codeOk = parsed?.code === "OK";

      if (response.ok && codeOk && !hasSpamFlag) {
        setStatus("success");
        setFormError("");
        form.reset();
        setMessageLength(0);
      } else {
        setStatus("error");
        if (messageText.trim() && !hasSpamFlag) {
          setFormError(messageText);
        } else {
          setFormError("提交未成功，请稍后重试，或直接发送邮件联系编辑部。");
        }
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交没有成功，请检查网络后重试。");
    }
  };

  return (
    <form
      id="feedback-form"
      data-readdy-form
      action={FORM_ENDPOINT}
      method="post"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
            怎么称呼你
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            autoComplete="name"
            placeholder="例如：阿澈"
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            联系邮箱
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="用于回复你的线索"
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
          反馈类型
        </label>
        <select
          id="topic"
          name="topic"
          defaultValue="补充线索"
          className="h-11 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        >
          <option value="补充线索">补充线索</option>
          <option value="内容勘误">内容勘误</option>
          <option value="希望整理的事件">希望整理的事件</option>
          <option value="合作与投稿">合作与投稿</option>
          <option value="其他建议">其他建议</option>
        </select>
      </div>

      <div className="mt-5">
        <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
          详细说明
        </label>
        <textarea
          id="message"
          name="message"
          required
          maxLength={500}
          rows={5}
          onChange={(e) => setMessageLength(e.target.value.length)}
          placeholder="请描述你要补充或纠正的内容，尽量附上公开来源，方便我们核实。"
          className="w-full resize-y rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        />
        <p className="mt-1.5 text-right text-xs text-foreground-400">{messageLength} / 500</p>
      </div>

      <div className="form-assist-area" aria-hidden="true">
        <label htmlFor="website_alt">备用联系地址</label>
        <input
          id="website_alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      {status === "success" && (
        <p className="mt-5 flex items-start gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
          <i className="ri-checkbox-circle-line mt-0.5" />
          已经收到你的反馈，编辑部会在核实后回复你，感谢你帮助这份档案变得更准确。
        </p>
      )}

      {status === "error" && (
        <p className="mt-5 flex items-start gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5" />
          {formError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-6 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
        {status === "submitting" ? "正在提交..." : "提交反馈"}
      </button>
    </form>
  );
}